import React, { CSSProperties } from "react";
import { Activity, Interval, Record } from "../activities/Activity";
import Canvas from "./Canvas";
import "./Graph.css";

interface GraphProps
{
    activity: Activity | null;
}

export default class Graph extends React.Component<GraphProps>
{
    render() {
        return (
            <Canvas initialScale={2} draw={this.draw.bind(this)}/>
        );
    }

    DataSeriesGraphs: DataSeriesGraph[];

    constructor(props: GraphProps)
    {
        super(props);

        this.DataSeriesGraphs = [];
    }

    override componentDidUpdate(prevProps: Readonly<GraphProps>, prevState: Readonly<{}>, snapshot?: any): void {
        this.DataSeriesGraphs = [
            new DataSeriesGraph("#FFE62D", new DataSeries(this.props.activity, record => record.Power)),
            new DataSeriesGraph("#FD4499", new DataSeries(this.props.activity, record => record.HeartRate)),
            new DataSeriesGraph("#12B8FF", new DataSeries(this.props.activity, record => record.Cadence))
        ]
    }

    draw(context: CanvasRenderingContext2D)
    {
        var cw = context.canvas.width / 2;
        var ch = context.canvas.height / 2;
        var activity = this.props.activity;

        context.clearRect(0, 0, cw, ch);

        if (!activity)
            return;

        const tes = activity.TotalElapsedSeconds;

        let lastPixelX = cw;
        if (lastPixelX > tes)
            lastPixelX = tes;

        let offsetSeconds = 0; // seconds at the pixel with x = 0
        
        if (tes > cw)
            offsetSeconds = tes - cw;
        
        //console.log("Draw ", activity.TotalSeconds, tes);
        
        this.drawGrid(context, offsetSeconds, cw, ch);

        for (let graph of this.DataSeriesGraphs)
            this.drawGraph(context, graph, lastPixelX, offsetSeconds, ch);
    }

    private drawGrid(context: CanvasRenderingContext2D, offsetSeconds: number, cw: number, ch: number)
    {
        const color = "#333333";
        context.strokeStyle = color;
        context.beginPath();
        const step = 60;
        context.lineWidth = 1;
        const startX = -(offsetSeconds + step) % step;

        for (let x = startX; x < cw; x += step)
        {
            context.moveTo(x + 0.5, 0);
            context.lineTo(x + 0.5, ch);
        }
        context.stroke();

        for (let x = startX; x < cw; x += step)
        {
            var seconds = offsetSeconds + x;
            const label = this.FormatSeconds(seconds);
            context.textBaseline = "top";
            context.font = "100 1rem helvetica";
            context.fillStyle = color;
            context.fillText(label, x + 2, 2);
        }
    }

    FormatSeconds(seconds: number)
    {
        const hours = Math.floor(seconds / 3600);
        const mins = Math.floor((seconds - hours * 3600) / 60);

        return hours == 0 ? `${mins}'` : `${hours}h ${mins}'`;
    }

    drawGraph(context: CanvasRenderingContext2D, graph: DataSeriesGraph, lastPixelX: number, offsetSeconds: number, ch: number)
    {
        context.strokeStyle = graph.Color;

        context.beginPath();
        context.moveTo(0, ch);

        context.shadowBlur = 10;
        context.shadowColor = graph.Color;

        for (let x = 0; x < lastPixelX; x++)
        {
            var y = graph.DataSeries.getValueAtTime(offsetSeconds + x) ?? 0;
            context.lineTo(x, ch - y - 0.5);
        }
        context.stroke();

        context.shadowBlur = 0;
        context.shadowColor = "transparent";
    }
}

class DataSeriesGraph
{
    Color: string;
    DataSeries: IDataSeries;

    constructor(color: string, dataSeries: IDataSeries)
    {
        this.Color = color;
        this.DataSeries = dataSeries;
    }
}

interface IDataSeries
{
    get RangeMin(): number;
    get RangeMax(): number;

    getValueAtTime(seconds: number): number | undefined;
}

type RecordFieldAccessor = (record: Record) => number | null;

class DataSeries implements IDataSeries
{
    static undefinedValue: number = 0;

    activity: Activity | null;
    recordFieldAccessor: RecordFieldAccessor;
    lastRecordIndex: number = 0;
    lastIntervalIndex: number = 0;

    constructor(activity: Activity | null, recordFieldAccessor: RecordFieldAccessor)
    {
        this.activity = activity;
        this.recordFieldAccessor = recordFieldAccessor;
    }

    getValueAtTime(seconds: number): number | undefined
    {
        if (this.activity == null)
            return DataSeries.undefinedValue;
        if (this.activity.Intervals.length == 0)
            return DataSeries.undefinedValue;
        

        var interval = this.findIntervalForTime(seconds);
        if (interval === null)
            return DataSeries.undefinedValue;

        var startTimeSeconds = this.activity.Intervals[0].Records[0].Time.getTime() / 1000;
        var record = interval.Records.find(r => r.Time.getTime() / 1000 >= startTimeSeconds + seconds);
        
        if (record === undefined)
            return DataSeries.undefinedValue;

        return this.recordFieldAccessor(record) ?? DataSeries.undefinedValue;
    }

    findIntervalForTime(seconds: number): Interval | null
    {
        if (this.activity === null)
            return null;

        var startTimeSeconds = this.activity.Intervals[0].Records[0].Time.getTime() / 1000;

        var interval = this.activity.Intervals.find(i => i.Records[0].Time.getTime() / 1000 <= startTimeSeconds + seconds && startTimeSeconds + seconds < i.Records[i.Records.length - 1].Time.getTime() / 1000);

        if (interval === undefined)
            return null;

        return interval;
    }

    RangeMin: number = 0;
    RangeMax: number = 0;
}